import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/errors'
import { Toaster, TooltipProvider } from '@/design'
import { reportMutationError } from '@/features/billing/upgrade-prompt-store'

/**
 * Query defaults: the API client already retries what is safe, so React Query does not retry 4xx.
 * The mutation cache reports every failure to the plan-limit handler, so a `PLAN_LIMIT_REACHED`
 * anywhere opens the one upgrade prompt (launch plan §5).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({ onError: reportMutationError }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status > 0 && error.status < 500) return false
          return failureCount < 1
        },
      },
      mutations: { retry: false },
    },
  })
}

export function AppProviders({
  children,
  client,
}: {
  readonly children: React.ReactNode
  readonly client?: QueryClient
}) {
  const [queryClient] = useState(() => client ?? createQueryClient())
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {children}
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  )
}
